import { usePostHog } from '@posthog/react';
import DrawerPortal from 'components/drawer/DrawerPortal';
import { ReactNode, useMemo } from 'react';
import { dispatch, useSelector } from 'store';
import { useGetNotificationsListQuery } from 'store/api/notifications';
import { closeTemporaryDrawer, toggleTemporaryDrawer } from 'store/reducers/temporaryDrawer';

import { Notifications as NotificationsIcon } from 'components/icons';
import { useIntl } from 'react-intl';

import DrawerContent from './Notifications/DrawerContent';
import StyledHeaderButton, { HeaderButtonCount } from './StyledHeaderButton';

const Notifications: React.FC = () => {
    const intl = useIntl();
    const posthog = usePostHog();
    const isOpen = useSelector((state) => state.temporaryDrawer.activeDrawerId === 'notifications');

    // Refetch notifications every 4 min
    const { data: notifications, isLoading } = useGetNotificationsListQuery({}, { pollingInterval: 240000 });

    const [unreadCount, unreadImportantCount] = useMemo(() => {
        let unreadImportantCount = 0;
        let unreadCount = 0;
        (notifications ?? [])?.forEach((val) => {
            if (val.isImportant && !val.readAt) unreadImportantCount += 1;
            if (!val.readAt) unreadCount += 1;
        });
        return [unreadCount, unreadImportantCount];
    }, [notifications]);

    const toggleDrawer = () => {
        if (isOpen) dispatch(closeTemporaryDrawer());
        else {
            dispatch(toggleTemporaryDrawer('notifications'));
            posthog.capture('notifications_opened', { unread_count: unreadCount });
        }
    };

    return (
        <>
            <StyledHeaderButton
                aria-label={intl.formatMessage({ id: 'notifications.notifications' })}
                onClick={() => toggleDrawer()}
                disabled={isLoading}
            >
                {/* The dot carries the important count the drawer breaks down; the number beside
                    it is every unread one. Same split as the tasks button. */}
                <NotificationIconWrap hasUnread={unreadImportantCount > 0}>
                    <NotificationsIcon className="text-[16px]" />
                </NotificationIconWrap>
                <HeaderButtonCount count={isLoading ? undefined : unreadCount} />
            </StyledHeaderButton>
            <DrawerPortal
                content={<DrawerContent notifications={notifications ?? []} />}
                drawerId="notifications"
                applyColor="secondary"
                type="temporary"
                width={715}
                widensBelowSm
            />
        </>
    );
};

/** A header action's icon, with the dot that says there is something urgent behind it. */
export const NotificationIconWrap = ({ hasUnread, children }: { hasUnread: boolean; children: ReactNode }) => (
    <span className="relative flex h-fit w-fit items-center">
        {children}
        {hasUnread && <span className="bg-error absolute -top-px -right-px size-2 rounded-full" />}
    </span>
);

export default Notifications;
